import {
  EditablePropertyData,
  EditableSubjectData,
  SharedEditablePropertyData,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../types.ts';
import { getBumpedSyncState } from '../../utils/getBumpedSyncState.ts';

export const getChangedEntityFromEditableData = <
  P extends SubjectPropertyDataState | CompPropertyDataState,
>(
  current: P,
  editableData: Partial<
    P extends SubjectPropertyDataState ? EditableSubjectData : EditablePropertyData
  >,
): { id: P['id']; changes: P } => {
  const { id } = current;
  const isSubject = id === 'subject';

  const propertyDataKey = isSubject ? 'subjectData' : 'compData';
  const propertyData = isSubject
    ? (current as SubjectPropertyDataState).subjectData
    : (current as CompPropertyDataState).compData;

  const currentEditableData = propertyData.editableData;

  const { improvements, propertyIdentification, site, transaction } = editableData;

  const changedSharedEditableData: SharedEditablePropertyData = {
    improvements: {
      ...currentEditableData.improvements,
      ...improvements,
    },
    propertyIdentification: {
      ...currentEditableData.propertyIdentification,
      ...propertyIdentification,
    },
    site: {
      ...currentEditableData.site,
      ...site,
    },
    transaction: {
      ...currentEditableData.transaction,
      ...transaction,
    },
  };

  const changedEditableData: Partial<EditablePropertyData & EditableSubjectData> =
    changedSharedEditableData;

  const { manualAdjustments } = editableData as EditablePropertyData & EditableSubjectData;
  const { manualAdjustments: currentManualAdjustments } = currentEditableData as
    | EditablePropertyData
    | EditableSubjectData;

  changedEditableData.manualAdjustments = {
    ...currentManualAdjustments,
    ...manualAdjustments,
  };

  if (isSubject) {
    const { opinionOfSiteValue, siteValueCommentary, arv } = editableData as EditableSubjectData;
    const {
      opinionOfSiteValue: currentOpinionOfSiteValue,
      siteValueCommentary: currentSiteValueCommentary,
      arv: currentArv,
    } = currentEditableData as EditableSubjectData;

    changedEditableData.opinionOfSiteValue = opinionOfSiteValue ?? currentOpinionOfSiteValue;
    changedEditableData.siteValueCommentary = siteValueCommentary ?? currentSiteValueCommentary;

    if (arv) {
      changedEditableData.arv = {
        ...currentArv,
        ...arv,
        manualAdjustments: {
          ...currentArv?.manualAdjustments,
          ...arv.manualAdjustments,
        },
      };
    }
  }

  const changes = {
    id,
    syncState: getBumpedSyncState(current.syncState),
    [propertyDataKey]: {
      ...propertyData,
      editableData: {
        ...currentEditableData,
        ...changedEditableData,
      },
    },
  } as P;

  return { id, changes };
};
